import { Assignable } from '../common/assignable'

/**
 * Application feature, which can be used or executed by the account
 */
export class Feature extends Assignable {
  constructor (data = {}) {
    super()
    this.assign({
      isEnabled: true,
      isSecure: true,
      ...data
    })
  }

  /**
   * Unique name of the feature
   * @type {String}
   */
  name

  /**
   * Feature description
   * @type {String}
   */
  description

  /**
   * Environment on which the feature should be available
   * @type {Environment}
   */
  environment

  /**
   * Default access to the feature. If true, the feature is always allowed, otherwise it requires explicit permission
   * @type {Boolean}
   */
  isSecure

  /**
   * Used to globally enable or disable the feature for all accounts.
   * In code rather use `isDisabled` as it looks up the hierarchy whether
   * parents are enabled as well.
   * @type {Boolean}
   */
  isEnabled

  /**
   * Indicates that this feature, when granted to account,
   * is a requirement rather than permission.
   * @type {Boolean}
   */
  isRequirement

  /**
   * Recursively checks whether the feature is disabled -
   * either directly or by one of its parents
   * @type {Boolean}
   */
  get isDisabled () {
    return !this.isEnabled || (this.parent ? this.parent.isDisabled : false)
  }

  /**
   * List of features which must have been granted to the account,
   * to avail of this feature as well
   * @type {Array[String]}
   */
  requires

  /**
   * If these features have been granted to the account,
   * this feature will not be available (the opposite of {@link requires}).
   * Additionally, the feature won't be visible in the editor.
   * @type {Array[String]}
   */
  notApplicableWhen

  /**
   * Child features
   * @type {Array[Feature]}
   */
  features

  /**
   * Returns true if feature has child features
   * @type {Boolean}
   */
  get hasChildren () {
    const { length } = this.features || []
    return length > 0
  }

  /**
   * Parent feature
   * @type {Feature}
   * @description RUNTIME PROPERTY
   */
  parent

  /**
   * Indicates that the feature is applicable under the current security context
   * @type {Boolean}
   * @description RUNTIME PROPERTY
   */
  isApplicable

  /**
   * Overrides serialization to prevent serializing of certain
   * runtime-only properties
   * @returns {Object}
   */
  toJSON () {
    const result = { ...this }
    delete result.parent
    delete result.isApplicable
    return result
  }

  /**
   * Creates a deep clone of the feature tree
   * @param parent Parent feature
   */
  clone (parent) {
    const data = { ...this, parent, isApplicable: undefined }
    const feature = new Feature(data)
    if (this.hasChildren) {
      feature.features = this.features.map(f => f.clone(feature))
    }
    return feature
  }

  /**
   * Returns a flat list of feature and its children
   */
  flat () {
    if (this.hasChildren) {
      return [this, ...this.features.flatMap(feature => feature.flat())]
    } else {
      return [this]
    }
  }

  /**
   * Traverses the feature tree
   * running the specified handler on each feature.
   * If handler returns truthy value on a node,
   * traverse is stopped and the value is returned.
   * @param {Function} handler Handler to run on each feature
   * @returns {any} Result returned by the handler on any of the traversed features
   */
  traverse (handler) {
    let result = handler(this)
    if (!result) {
      if (this.hasChildren) {
        for (const child of this.features) {
          result = child.traverse(handler)
          if (result) {
            break
          }
        }
      }
    }
    return result
  }

  /**
   * Finds a feature with the specified name
   * @param {String} name Feature name
   * @returns {Feature}
   */
  find (name) {
    return this.traverse(feature =>
      (feature.name === name ? feature : undefined))
  }
}
